import { useCallback, useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          size?: "normal" | "compact" | "invisible";
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
        },
      ) => string;
      reset(widgetId?: string): void;
      remove?(widgetId: string): void;
      execute(widgetId: string): void;
    };
  }
}

const scriptUrl =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileLoader: Promise<void> | null = null;

const loadTurnstileScript = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("TURNSTILE_NOT_READY"));
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  if (!turnstileLoader) {
    turnstileLoader = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        turnstileLoader = null;
        reject(new Error("TURNSTILE_NOT_READY"));
      };
      document.head.appendChild(script);
    });
  }

  return turnstileLoader;
};

type TurnstileOptions = {
  size?: "normal" | "compact";
  theme?: "light" | "dark" | "auto";
};

export function useTurnstileToken(options: TurnstileOptions = {}) {
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cleanupWidget = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (widgetIdRef.current && window.turnstile?.remove) {
      window.turnstile.remove(widgetIdRef.current);
    }
    widgetIdRef.current = null;
    setIsReady(false);
    setToken(null);
  }, []);

  const handleError = useCallback(() => {
    setError("TURNSTILE_VERIFICATION_FAILED");
    setToken(null);
    if (widgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  const handleSuccess = useCallback((newToken: string) => {
    setError(null);
    setToken(newToken);
  }, []);

  const renderWidget = useCallback(() => {
    if (!containerRef.current || widgetIdRef.current || !window.turnstile) {
      return;
    }

    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      setError("TURNSTILE_NOT_CONFIGURED");
      return;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      size: options.size ?? "normal",
      theme: options.theme ?? "light",
      callback: handleSuccess,
      "error-callback": handleError,
      "expired-callback": () => {
        setToken(null);
        if (widgetIdRef.current && window.turnstile?.reset) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    });

    setIsReady(true);
    setIsLoading(false);
  }, [handleError, handleSuccess, options.size, options.theme]);

  const ensureWidget = useCallback(async () => {
    if (!containerRef.current) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      await loadTurnstileScript();
      renderWidget();
    } catch (err) {
      console.error(err);
      setError("TURNSTILE_NOT_READY");
      setIsLoading(false);
    }
  }, [renderWidget]);

  useEffect(() => {
    ensureWidget();
    return () => {
      cleanupWidget();
    };
  }, [ensureWidget, cleanupWidget]);

  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        containerRef.current = null;
        cleanupWidget();
        return;
      }

      containerRef.current = node;
      ensureWidget();
    },
    [ensureWidget, cleanupWidget],
  );

  const reset = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    setToken(null);
    if (widgetIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return {
    ref: setContainerRef,
    token,
    isReady,
    isLoading,
    error,
    reset,
  };
}
